import React, { useMemo } from 'react'
import type { ColDef, GridOptions, ICellRendererParams, ValueGetterParams } from 'ag-grid-community'
import { CustomAgGrid } from '@/components/CustomAgGrid.tsx'
import { CHANGED_CELL_STYLE, displayValue, formatFieldChange } from '@/utils/changed-fields.ts'
import { toColumnHeaderName } from '@/utils/column-labels.ts'
import type { UserMessageFieldChange, UserMessageRow } from './user-messages.def.ts'

/**
 * Column order follows the payload: every column present in a row snapshot
 * first, then any column that only shows up in `changedFields` (a value that was
 * cleared may be missing from the snapshot).
 */
const collectColumnNames = (rows: UserMessageRow[]): string[] => {
  const names = new Set<string>()

  for (const row of rows) {
    Object.keys(row.values ?? {}).forEach(name => names.add(name))
  }

  for (const row of rows) {
    row.changedFields?.forEach(field => names.add(field.columnName))
  }

  return [...names]
}

const findChangedField = (row: UserMessageRow, columnName: string): UserMessageFieldChange | undefined =>
  row.changedFields?.find(field => field.columnName === columnName)

interface Props {
  rows: UserMessageRow[]
  height?: number | string
}

/**
 * Renders the tabular detail attached to a notification, mirroring the audit log
 * row grid: one column per payload column, `old → new` text plus a red cell for
 * anything listed in `changedFields`.
 */
export const UserMessageRowsGrid: React.FC<Props> = ({ rows, height = 480 }) => {
  const columnDefs = useMemo<ColDef<UserMessageRow>[]>(
    () => collectColumnNames(rows).map<ColDef<UserMessageRow>>(columnName => ({
      colId: `values:${columnName}`,
      headerName: toColumnHeaderName(columnName),
      minWidth: 160,
      // The displayed text is produced here so filtering, sorting and Excel export
      // all match what is on screen (including the "old → new" form).
      valueGetter: ({ data }: ValueGetterParams<UserMessageRow>) => {
        if (!data) return ''
        const changed = findChangedField(data, columnName)
        if (changed) return formatFieldChange(changed)
        return displayValue(data.values?.[columnName])
      },
      tooltipValueGetter: ({ value }) => (value as string) ?? '',
      cellRenderer: ({ data, value }: ICellRendererParams<UserMessageRow>) => {
        const changed = data ? findChangedField(data, columnName) : undefined
        return (
          <span style={changed ? CHANGED_CELL_STYLE : undefined}>
            {(value as string) ?? ''}
          </span>
        )
      },
    })),
    [rows],
  )

  const gridOptions = useMemo<GridOptions<UserMessageRow>>(() => ({
    suppressCellFocus: true,
    enableCellTextSelection: true,
    pagination: rows.length > 20,
    rowSelection: {
      mode: 'multiRow',
      checkboxes: false,
      headerCheckbox: false,
      enableClickSelection: false,
    },
  }), [rows.length])

  return (
    <CustomAgGrid<UserMessageRow>
      rowData={rows}
      columnDefs={columnDefs}
      gridOptions={gridOptions}
      height={height}
      autoHeaderHeight
    />
  )
}
